import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import type { Task } from '../types/task';
import { computeTaskStatus } from '../utils/taskStatus';
import TaskAssignee from './TaskAssignee';
import ArchiveIcon from './icons/ArchiveIcon';
import UnarchiveIcon from './icons/UnarchiveIcon';

export interface TaskCardProps {
  task: Task;
  defaultShortName: string;
  onTaskChanged: (updated: Task) => void;
  onError: (message: string) => void;
  onWorkstreamChanged: () => void;
  // Optional drag wiring from a parent sortable wrapper. When provided,
  // these are applied to the card's root element so the parent can
  // integrate with @dnd-kit/sortable without TaskCard itself calling
  // useSortable (which would register a duplicate draggable node if the
  // card is also rendered inside a DragOverlay).
  rootRef?: React.Ref<HTMLDivElement>;
  rootAttributes?: DraggableAttributes;
  rootListeners?: DraggableSyntheticListeners;
  rootStyle?: React.CSSProperties;
}

// Determine the card background color based on status precedence:
// 1. Archived (red) — trumps everything
// 2. PR-based status (purple/green/grey) from computeTaskStatus
// 3. Parallel + Pending (orange)
// 4. White (default)
function getCardColor(task: Task): string {
  if (task.Deleted) {
    return '#fef2f2'; // pale red (Tailwind red-50) — consistent with other pale card tints
  }
  const status = computeTaskStatus(task);
  if (status) {
    if (status.variant === 'merged') {
      return '#fbefff'; // purple
    }
    if (status.variant === 'open' || status.variant === 'partial-open') {
      return '#dafbe1'; // green
    }
    if (status.variant === 'draft') {
      return '#f6f8fa'; // grey
    }
  }
  if (task.Parallel && task.State === 'Pending') {
    return '#ffd8a8'; // orange
  }
  return '#fff'; // white
}

function getFailureMessage(task: Task): string | null {
  const lastStatus = (task.LastTurnStatus ?? task.Status ?? '').trim().toLowerCase();
  if (lastStatus !== 'failed') {
    return null;
  }
  return 'Task failed';
}

function EditIcon({ size = 14 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function ParallelToggleButton({
  enabled,
  disabled,
  onClick,
}: {
  enabled: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={enabled ? 'Disable parallel execution' : 'Enable parallel execution'}
      disabled={disabled}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        display: 'inline-block',
        width: '28px',
        height: '16px',
        borderRadius: '8px',
        backgroundColor: enabled ? '#28a745' : '#ccc',
        position: 'relative',
        verticalAlign: 'middle',
        flexShrink: 0,
        border: 'none',
        padding: 0,
        cursor: disabled ? 'wait' : 'pointer',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: enabled ? '14px' : '2px',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          transition: 'left 0.2s',
          display: 'block',
        }}
      />
    </button>
  );
}

export default function TaskCard({
  task,
  defaultShortName,
  onTaskChanged,
  onError,
  onWorkstreamChanged,
  rootRef,
  rootAttributes,
  rootListeners,
  rootStyle,
}: TaskCardProps) {
  const taskLabel = task.TaskNumber
    ? `${defaultShortName.toUpperCase()}-${task.TaskNumber}`
    : task.TaskID.substring(0, 8);

  const taskUrl = task.WorkstreamID
    ? `/workstreams/tasks/${encodeURIComponent(task.WorkstreamID)}/${encodeURIComponent(task.TaskID)}`
    : `/tasks/${encodeURIComponent(task.TaskID)}`;

  const cardColor = getCardColor(task);
  const failureMessage = getFailureMessage(task);
  const location = useLocation();
  const linkState = { from: location.pathname };

  // Archived cards use a pale red tint background with a dark red title.
  // Icons and the blue link remain unchanged (they contrast fine on #fef2f2).
  const isArchived = !!task.Deleted;
  const titleColor = isArchived ? '#991b1b' : '#24292f';

  const workstreamID = task.WorkstreamID ?? '';
  const version = task.Version ?? 0;

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.Title);
  const [savingTitle, setSavingTitle] = useState(false);
  const [savingParallel, setSavingParallel] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitleDraft(task.Title);
  }, [task.Title]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  const patchTask = useCallback(
    async (fields: Record<string, unknown>): Promise<Task | null> => {
      if (!workstreamID) return null;
      const resp = await fetch(
        `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(task.TaskID)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: version, Fields: fields }),
        },
      );
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update task');
      }
      return (await resp.json()) as Task;
    },
    [workstreamID, task.TaskID, version],
  );

  const saveTitle = useCallback(async () => {
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === task.Title) {
      setEditingTitle(false);
      setTitleDraft(task.Title);
      return;
    }
    setSavingTitle(true);
    try {
      const updated = await patchTask({ Title: trimmed });
      if (updated) onTaskChanged(updated);
      setEditingTitle(false);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Failed to save title');
    } finally {
      setSavingTitle(false);
    }
  }, [titleDraft, task.Title, patchTask, onTaskChanged, onError]);

  const cancelTitle = useCallback(() => {
    setTitleDraft(task.Title);
    setEditingTitle(false);
  }, [task.Title]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Always stop propagation so the parent sortable's keyboard sensor
      // (Enter/Space/arrow keys) doesn't try to start a drag from the
      // focused input.
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        saveTitle();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelTitle();
      }
    },
    [saveTitle, cancelTitle],
  );

  // Shared keydown handler for interactive child controls (buttons, links).
  // Stops Enter/Space/arrow keys from bubbling up to the parent sortable's
  // KeyboardSensor, which would otherwise start a drag instead of letting
  // the focused control handle the keystroke normally.
  const stopKeyPropagation = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const handleToggleParallel = useCallback(async () => {
    if (savingParallel) return;
    setSavingParallel(true);
    try {
      const updated = await patchTask({ Parallel: !task.Parallel });
      if (updated) onTaskChanged(updated);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Failed to toggle parallel');
    } finally {
      setSavingParallel(false);
    }
  }, [savingParallel, task.Parallel, patchTask, onTaskChanged, onError]);

  const handleArchiveToggle = useCallback(async () => {
    if (archiveBusy || !workstreamID) return;
    const wasDeleted = !!task.Deleted;
    const verb = wasDeleted ? 'unarchive' : 'archive';
    setArchiveBusy(true);
    try {
      const url = wasDeleted
        ? `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(task.TaskID)}/undelete`
        : `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(task.TaskID)}`;
      const method = wasDeleted ? 'POST' : 'DELETE';
      const resp = await fetch(url, { method });
      if (!resp.ok && resp.status !== 204) {
        const text = await resp.text();
        onError(text || `Failed to ${verb} task`);
        return;
      }
      onTaskChanged({ ...task, Deleted: !wasDeleted });
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : `Failed to ${verb} task`);
    } finally {
      setArchiveBusy(false);
    }
  }, [archiveBusy, workstreamID, task, onTaskChanged, onError]);

  const handleAssigneeSaved = useCallback(
    (updated: { AssignedToAI: boolean; AssignedToTenantID: string; Version: number }) => {
      onTaskChanged({
        ...task,
        AssignedToAI: updated.AssignedToAI,
        AssignedToTenantID: updated.AssignedToTenantID,
        Version: updated.Version,
      });
      // Assigning to AI can auto-pause the workstream; refresh header state.
      if (updated.AssignedToAI && !task.AssignedToAI) {
        onWorkstreamChanged();
      }
    },
    [task, onTaskChanged, onWorkstreamChanged],
  );

  return (
    <div
      ref={rootRef}
      data-task-id={task.TaskID}
      {...(rootAttributes ?? {})}
      {...(rootListeners ?? {})}
      style={{
        border: isArchived ? '1px solid #fecaca' : '1px solid #d0d7de',
        borderRadius: '6px',
        padding: '8px 10px',
        backgroundColor: cardColor,
        marginBottom: '8px',
        ...rootStyle,
      }}
    >
      {/* Top row: title + edit icon */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4px',
        }}
      >
        {editingTitle ? (
          <input
            ref={titleInputRef}
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            onBlur={saveTitle}
            onPointerDown={(e) => e.stopPropagation()}
            disabled={savingTitle}
            style={{
              flex: 1,
              fontSize: '13px',
              fontWeight: 500,
              padding: '2px 6px',
              border: '1px solid #0969da',
              borderRadius: '4px',
              outline: 'none',
              minWidth: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: '13px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              color: titleColor,
              textDecoration: isArchived ? 'line-through' : 'none',
            }}
          >
            {task.Title || 'Untitled'}
          </span>
        )}
        {!editingTitle && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={stopKeyPropagation}
            onClick={(e) => {
              e.stopPropagation();
              setEditingTitle(true);
            }}
            title="Edit title"
            aria-label="Edit title"
            style={{
              background: 'none',
              border: 'none',
              padding: '0 2px',
              cursor: 'pointer',
              color: '#57606a',
              flexShrink: 0,
              lineHeight: 1,
            }}
          >
            <EditIcon />
          </button>
        )}
      </div>

      {/* Bottom row: assignee, parallel toggle, task link, archive */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: '6px',
          gap: '6px',
        }}
      >
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={stopKeyPropagation}
          style={{ display: 'inline-flex' }}
        >
          <TaskAssignee
            assignedToAI={!!task.AssignedToAI}
            assignedToTenantID={task.AssignedToTenantID ?? ''}
            workstreamID={workstreamID}
            taskID={task.TaskID}
            version={version}
            onSaved={handleAssigneeSaved}
            onError={onError}
            compact
          />
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '11px', color: '#57606a' }}>Parallel:</span>
          <ParallelToggleButton
            enabled={!!task.Parallel}
            disabled={savingParallel}
            onClick={handleToggleParallel}
          />
        </span>
        <Link
          to={taskUrl}
          state={linkState}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={stopKeyPropagation}
          style={{
            fontSize: '12px',
            color: '#0969da',
            textDecoration: 'none',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            marginLeft: 'auto',
          }}
        >
          {taskLabel}
        </Link>
        <button
          type="button"
          title={isArchived ? 'Unarchive task' : 'Archive task'}
          aria-label={isArchived ? 'Unarchive task' : 'Archive task'}
          disabled={archiveBusy}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={stopKeyPropagation}
          onClick={(e) => {
            e.stopPropagation();
            handleArchiveToggle();
          }}
          style={{
            background: 'none',
            border: 'none',
            padding: '0',
            cursor: archiveBusy ? 'wait' : 'pointer',
            color: '#57606a',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {isArchived ? <UnarchiveIcon size={14} /> : <ArchiveIcon size={14} />}
        </button>
      </div>

      {failureMessage && (
        <div
          title={failureMessage}
          style={{
            marginTop: '6px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: '#cf222e',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            overflow: 'hidden',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {failureMessage}
          </span>
        </div>
      )}
    </div>
  );
}
